import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Divider,
  useTheme,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { updateUser } from '../../store/slices/authSlice.js'
import toast from 'react-hot-toast'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useLoading } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { formatDate } from '../../utils/formatters'

const EmployeeProfile = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const [editing, setEditing] = useState(false)
  const [selectedTab, setSelectedTab] = useState(0)
  const [openAvatarDialog, setOpenAvatarDialog] = useState(false)
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false)
  const [profileData, setProfileData] = useState({
    personalInfo: {
      employeeId: 'EMP00123',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@company.com',
      phone: '+1 234 567 8900',
      dateOfBirth: '1990-05-15',
      gender: 'male',
      bloodGroup: 'O+',
      maritalStatus: 'single',
      nationality: 'American',
    },
    employmentInfo: {
      designation: 'Senior Software Engineer',
      department: 'Engineering',
      reportingTo: 'Sarah Johnson',
      employeeType: 'Full-time',
      employmentStatus: 'Active',
      dateOfJoining: '2020-03-15',
      probationEndDate: '2020-09-15',
      noticePeriod: '60 days',
      workLocation: 'New York Office',
      workSchedule: '9:00 AM - 6:00 PM',
    },
    professionalInfo: {
      qualification: 'Masters in Computer Science',
      university: 'Stanford University',
      yearOfPassing: '2012',
      totalExperience: '10 years',
      previousCompany: 'Tech Solutions Inc.',
      skills: ['React', 'Node.js', 'Python', 'AWS', 'Docker', 'MongoDB'],
      certifications: ['AWS Certified', 'React Professional'],
    },
    contactInfo: {
      address: '123 Main Street, New York, NY 10001',
      emergencyContactName: 'Jane Doe',
      emergencyContactPhone: '+1 234 567 8911',
      emergencyContactRelation: 'Spouse',
    },
    documents: [
      { id: 1, name: 'Employment Contract', type: 'PDF', uploadDate: '2020-03-15' },
      { id: 2, name: 'ID Proof', type: 'PDF', uploadDate: '2020-03-20' },
      { id: 3, name: 'Educational Certificates', type: 'ZIP', uploadDate: '2020-03-25' },
      { id: 4, name: 'Experience Letters', type: 'PDF', uploadDate: '2020-04-01' },
    ],
    activityLog: [
      { id: 1, action: 'Profile Updated', timestamp: '2024-01-15 14:30', details: 'Updated contact information' },
      { id: 2, action: 'Password Changed', timestamp: '2024-01-10 11:15', details: 'Changed account password' },
      { id: 3, action: 'Document Uploaded', timestamp: '2024-01-05 16:45', details: 'Uploaded new certificate' },
      { id: 4, action: 'Profile Viewed', timestamp: '2024-01-01 09:30', details: 'HR viewed profile' },
    ],
  })
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})

  useEffect(() => {
    // Initialize form data with current profile data
    setFormData({
      ...profileData.personalInfo,
      ...profileData.contactInfo,
    })
  }, [])

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
  }

  const handleEditToggle = () => {
    if (editing) {
      // Reset form data when canceling edit
      setFormData({
        ...profileData.personalInfo,
        ...profileData.contactInfo,
      })
      setErrors({})
    }
    setEditing(!editing)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    if (!formData.emergencyContactPhone?.trim()) {
      newErrors.emergencyContactPhone = 'Emergency contact phone is required'
    }

    return newErrors
  }

  const handleSaveProfile = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      toast.error('Please fix the errors in the form')
      return
    }

    withLoading(async () => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Update Redux store
      const updatedUser = {
        ...user,
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
      }

      dispatch(updateUser(updatedUser))

      // Update local state
      setProfileData(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          gender: formData.gender,
          bloodGroup: formData.bloodGroup,
          maritalStatus: formData.maritalStatus,
          nationality: formData.nationality,
        },
        contactInfo: {
          ...prev.contactInfo,
          address: formData.address,
          emergencyContactName: formData.emergencyContactName,
          emergencyContactPhone: formData.emergencyContactPhone,
          emergencyContactRelation: formData.emergencyContactRelation,
        },
      }))

      toast.success('Profile updated successfully!')
      setEditing(false)
    })
  }

  const tabs = [
    { label: 'Personal Info', icon: <PersonIcon /> },
    { label: 'Employment', icon: <WorkIcon /> },
    { label: 'Professional', icon: <SchoolIcon /> },
    { label: 'Contact', icon: <LocationIcon /> },
    { label: 'Documents', icon: <AssignmentIcon /> },
    { label: 'Activity', icon: <HistoryIcon /> },
  ]

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  ]

  const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']
  const maritalStatusOptions = ['single', 'married', 'divorced', 'widowed']

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                My Profile
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Manage your personal and professional information
              </Typography>
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 3 }} sx={{ textAlign: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      fontSize: '2.5rem',
                      bgcolor: '#1976d2',
                      border: `4px solid ${theme.palette.background.paper}`,
                      boxShadow: 3,
                    }}
                    src={user?.avatar}
                  >
                    {user?.name?.charAt(0) || 'E'}
                  </Avatar>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                    onClick={() => setOpenAvatarDialog(true)}
                  >
                    <CameraIcon />
                  </IconButton>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box>
                  <Typography variant="h4" gutterBottom>
                    {profileData.personalInfo.firstName} {profileData.personalInfo.lastName}
                  </Typography>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {profileData.employmentInfo.designation}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Chip
                      icon={<BadgeIcon />}
                      label={`ID: ${profileData.personalInfo.employeeId}`}
                      variant="outlined"
                    />
                    <Chip
                      icon={<WorkIcon />}
                      label={profileData.employmentInfo.department}
                      variant="outlined"
                    />
                    <StatusChip status={profileData.employmentInfo.employmentStatus} />
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant={editing ? "outlined" : "contained"}
                    startIcon={editing ? <CancelIcon /> : <EditIcon />}
                    onClick={handleEditToggle}
                    fullWidth
                  >
                    {editing ? 'Cancel Edit' : 'Edit Profile'}
                  </Button>
                  {editing && (
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                      disabled={loading}
                      fullWidth
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<KeyIcon />}
                    onClick={() => setOpenPasswordDialog(true)}
                    fullWidth
                  >
                    Change Password
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        </Paper>

        <Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            {selectedTab === 0 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  Personal Information
                </Typography>

                {editing ? (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="First Name *"
                        value={formData.firstName || ''}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName || ''}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email *"
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start"><EmailIcon /></InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Phone *"
                        value={formData.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start"><PhoneIcon /></InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Date of Birth"
                          value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : null}
                          onChange={(newValue) => handleInputChange('dateOfBirth', newValue)}
                          slotProps={{ textField: { fullWidth: true, disabled: loading } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={formData.gender || ''}
                          label="Gender"
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          disabled={loading}
                        >
                          {genderOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>Blood Group</InputLabel>
                        <Select
                          value={formData.bloodGroup || ''}
                          label="Blood Group"
                          onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                          disabled={loading}
                        >
                          {bloodGroupOptions.map((group) => (
                            <MenuItem key={group} value={group}>
                              {group}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>Marital Status</InputLabel>
                        <Select
                          value={formData.maritalStatus || ''}
                          label="Marital Status"
                          onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                          disabled={loading}
                        >
                          {maritalStatusOptions.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Nationality"
                        value={formData.nationality || ''}
                        onChange={(e) => handleInputChange('nationality', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Employee ID"
                            secondary={profileData.personalInfo.employeeId}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Full Name"
                            secondary={`${profileData.personalInfo.firstName} ${profileData.personalInfo.lastName}`}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Email"
                            secondary={profileData.personalInfo.email}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Phone"
                            secondary={profileData.personalInfo.phone}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Date of Birth"
                            secondary={formatDate(profileData.personalInfo.dateOfBirth)}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Gender"
                            secondary={profileData.personalInfo.gender.charAt(0).toUpperCase() + profileData.personalInfo.gender.slice(1)}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Blood Group"
                            secondary={profileData.personalInfo.bloodGroup}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Marital Status"
                            secondary={profileData.personalInfo.maritalStatus.charAt(0).toUpperCase() + profileData.personalInfo.maritalStatus.slice(1)}
                            primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            )}

            {/* Employment Tab */}
            {selectedTab === 1 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkIcon />
                  Employment Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Designation"
                          secondary={profileData.employmentInfo.designation}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Department"
                          secondary={profileData.employmentInfo.department}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Reporting To"
                          secondary={profileData.employmentInfo.reportingTo}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Employee Type"
                          secondary={profileData.employmentInfo.employeeType}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Employment Status"
                          secondary={profileData.employmentInfo.employmentStatus}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Date of Joining"
                          secondary={formatDate(profileData.employmentInfo.dateOfJoining)}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Work Location"
                          secondary={profileData.employmentInfo.workLocation}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Work Schedule"
                          secondary={profileData.employmentInfo.workSchedule}
                          primaryTypographyProps={{ color: 'textSecondary', variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Additional Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">
                          Probation End Date
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(profileData.employmentInfo.probationEndDate)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">
                          Notice Period
                        </Typography>
                        <Typography variant="body1">
                          {profileData.employmentInfo.noticePeriod}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">
                          Total Tenure
                        </Typography>
                        <Typography variant="body1">
                          {Math.floor((new Date() - new Date(profileData.employmentInfo.dateOfJoining)) / (1000 * 60 * 60 * 24 * 365))} years
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Professional Tab */}
            {selectedTab === 2 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon />
                  Professional Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="textSecondary">Qualification</Typography>
                      <Typography variant="body1">{profileData.professionalInfo.qualification}</Typography>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="textSecondary">University/Institute</Typography>
                      <Typography variant="body1">{profileData.professionalInfo.university}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Year of Passing</Typography>
                      <Typography variant="body1">{profileData.professionalInfo.yearOfPassing}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="textSecondary">Total Experience</Typography>
                      <Typography variant="body1">{profileData.professionalInfo.totalExperience}</Typography>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="textSecondary">Previous Company</Typography>
                      <Typography variant="body1">{profileData.professionalInfo.previousCompany}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Skills</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {profileData.professionalInfo.skills.map((skill, index) => (
                          <Chip key={index} label={skill} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Contact Tab */}
            {selectedTab === 3 && (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon />
                  Contact Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">Current Address</Typography>
                      <Typography variant="body1">{profileData.contactInfo.address}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" gutterBottom>Emergency Contact</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                      <Typography variant="body1">{profileData.contactInfo.emergencyContactName}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Relation</Typography>
                      <Typography variant="body1">{profileData.contactInfo.emergencyContactRelation}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                      <Typography variant="body1">{profileData.contactInfo.emergencyContactPhone}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Documents Tab */}
            {selectedTab === 4 && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon />
                  Documents
                </Typography>
                <Grid container spacing={2}>
                  {profileData.documents.map((doc) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={doc.id}>
                      <Card variant="outlined">
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                              {doc.type}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {doc.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton size="small" color="primary">
                            <EditIcon />
                          </IconButton>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined" sx={{ height: '100%', borderStyle: 'dashed', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <CameraIcon color="primary" sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                        <Typography variant="body2" color="primary">Upload New Document</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Activity Tab */}
            {selectedTab === 5 && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon />
                  Activity Log
                </Typography>
                <List>
                  {profileData.activityLog.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem alignItems="flex-start">
                        <Avatar sx={{ mr: 2, bgcolor: 'grey.300' }}>
                          <HistoryIcon color="action" />
                        </Avatar>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1">{activity.action}</Typography>
                              <Typography variant="caption" color="textSecondary">{activity.timestamp}</Typography>
                            </Box>
                          }
                          secondary={activity.details}
                        />
                      </ListItem>
                      {index < profileData.activityLog.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default EmployeeProfile